import React, { useState, useEffect, useCallback } from 'react';
import {
  getRevenueAnalytics,
  getClientAnalytics,
  getOperationsAnalytics,
  getForecastAnalytics,
  getWorkerAnalytics
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DollarSign, Users, Activity, BarChart3, RefreshCw, LineChart
} from 'lucide-react';
import { toast } from 'sonner';

// Sub-components extracted for maintainability
import { OverviewTab } from './analytics/OverviewTab';
import { RevenueTab } from './analytics/RevenueTab';
import { ClientsTab } from './analytics/ClientsTab';
import { OperationsTab } from './analytics/OperationsTab';
import { ForecastTab } from './analytics/ForecastTab';

export default function AnalyticsDashboardPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('monthly');
  
  // Data states
  const [revenueData, setRevenueData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [operationsData, setOperationsData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [workerData, setWorkerData] = useState(null);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [revenue, clients, operations, forecast, workers] = await Promise.all([
        getRevenueAnalytics({ period }),
        getClientAnalytics(),
        getOperationsAnalytics(),
        getForecastAnalytics({ months: 6 }),
        getWorkerAnalytics()
      ]);
      setRevenueData(revenue.data);
      setClientData(clients.data);
      setOperationsData(operations.data);
      setForecastData(forecast.data);
      setWorkerData(workers.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="analytics-title">Business Intelligence</h1>
          <p className="text-muted-foreground">Advanced analytics and insights for data-driven decisions</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]" data-testid="period-selector">
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadAllData} disabled={loading} data-testid="refresh-analytics">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="overview" className="flex items-center gap-1" data-testid="tab-overview">
            <BarChart3 className="w-4 h-4" /> Overview
          </TabsTrigger>
          <TabsTrigger value="revenue" className="flex items-center gap-1" data-testid="tab-revenue">
            <DollarSign className="w-4 h-4" /> Revenue
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex items-center gap-1" data-testid="tab-clients">
            <Users className="w-4 h-4" /> Clients
          </TabsTrigger>
          <TabsTrigger value="operations" className="flex items-center gap-1" data-testid="tab-operations">
            <Activity className="w-4 h-4" /> Operations
          </TabsTrigger>
          <TabsTrigger value="forecast" className="flex items-center gap-1" data-testid="tab-forecast">
            <LineChart className="w-4 h-4" /> Forecast
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab loading={loading} revenueData={revenueData} clientData={clientData} operationsData={operationsData} workerData={workerData} />
        </TabsContent>

        <TabsContent value="revenue" className="mt-6">
          <RevenueTab loading={loading} revenueData={revenueData} period={period} />
        </TabsContent>

        <TabsContent value="clients" className="mt-6">
          <ClientsTab loading={loading} clientData={clientData} />
        </TabsContent>

        <TabsContent value="operations" className="mt-6">
          <OperationsTab loading={loading} operationsData={operationsData} workerData={workerData} />
        </TabsContent>

        <TabsContent value="forecast" className="mt-6">
          <ForecastTab loading={loading} forecastData={forecastData} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
